import { useLayoutEffect, useRef } from 'react';

const CONTENT_GAP = 10;
const ARTWORK_GAP = 12;

const setPixels = (element, property, value) => {
    const next = `${Math.round(value * 100) / 100}px`;
    if (element.style.getPropertyValue(property) !== next) {
        element.style.setProperty(property, next);
    }
};

// Keep Play in the left stack while artwork and trailing actions size themselves.
// Measuring the real controls also handles saved/download states and larger text.
export const updateTrackRowLayout = (row) => {
    const byline = row.querySelector('.trackInfoByline');
    const play = row.querySelector('.trackInfoPlayPill');
    const actions = row.querySelector('.trackInfoFooterActions, .trackInfoSelectionIndicator');
    const artwork = row.querySelector('.trackInfoEpisodeImageButton');
    const width = row.getBoundingClientRect().width;
    if (!byline || !play || width <= 0) return;
    row.dataset.layoutReady = 'true';

    const art = artwork?.getBoundingClientRect();
    const bylineHeight = byline.getBoundingClientRect().height;
    const playTop = bylineHeight + CONTENT_GAP;
    const artworkInset = art ? art.width + ARTWORK_GAP : 0;
    const playWidth = art && playTop < art.height + CONTENT_GAP ? width - artworkInset : width;
    setPixels(row, '--track-row-play-width', Math.max(0, playWidth));

    const playSize = play.getBoundingClientRect();
    const actionsSize = actions?.getBoundingClientRect();
    let actionsTop = 0;
    if (actionsSize?.height > 0) {
        actionsTop = playTop + (playSize.height - actionsSize.height) / 2;
        if (art) actionsTop = Math.max(actionsTop, art.height + CONTENT_GAP);
        if (width - actionsSize.width < playSize.width + CONTENT_GAP) {
            actionsTop = Math.max(actionsTop, playTop + playSize.height + CONTENT_GAP);
        }
    }

    setPixels(row, '--track-row-play-top', playTop);
    setPixels(row, '--track-row-actions-top', actionsTop);
    setPixels(row, '--track-row-height', Math.max(
        bylineHeight,
        playTop + playSize.height,
        actionsTop + (actionsSize?.height || 0),
        art?.height || 0,
    ));
};

export default function useTrackRowLayout() {
    const ref = useRef(null);

    // Reconnect after a render because artwork, selection controls, or the
    // progress row can be added/removed without changing the row's identity.
    useLayoutEffect(() => {
        const row = ref.current;
        if (!row) return undefined;
        let active = true;
        const update = () => { if (active) updateTrackRowLayout(row); };
        update();

        const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update);
        [row, ...row.querySelectorAll('.trackInfoByline, .trackInfoPlayPill, .trackInfoFooterActions, .trackInfoSelectionIndicator, .trackInfoEpisodeImageButton')]
            .forEach((element) => observer?.observe(element));
        window.addEventListener('resize', update);
        document.fonts?.ready.then(update);
        return () => {
            active = false;
            observer?.disconnect();
            window.removeEventListener('resize', update);
        };
    });

    return ref;
}
